import { z } from '@repo/schema'
import { GetCompanySchema } from '@repo/schema/company'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import { GetEmailSchema } from '@repo/schema/email'
import { pageDataSchema } from '@repo/schema/page-data'
import { GetPaginationSchema } from '@repo/schema/pagination'
import {
  addPhoneWithCountryValidation,
  createPhoneNumberFieldSchema,
  OptionalPhoneCountryCodeSchema,
} from '@repo/schema/phone'
import { GetRoleSchema } from '@repo/schema/role'
import { GetStatusSchema } from '@repo/schema/status'
import { GetLastAuthSchema, GetUserSchema, GetUserStatusSchema } from '@repo/schema/user'
import { UpFetch } from '@repo/upfetch'

import { createProcedure } from '../../lib/procedure'

// A User's stored data for one page: table settings, Saved Views and layouts, keyed by value.
const PageDataPathSchema = z.object({ page: z.string(), userId: z.number().or(z.string()) })
export type PageDataPath = z.infer<typeof PageDataPathSchema>

const pageDataPath = (path: PageDataPath) => ['users', path.userId, 'pages', path.page, 'data']

export const createUsersRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)
  const userPhoneInputSchema = z.object({
    phone: createPhoneNumberFieldSchema().optional(),
    phone_country: OptionalPhoneCountryCodeSchema.optional(),
  })

  return {
    // POST /users/:id/permissions/attach
    attachPermission: p
      .path(z.object({ userId: z.number() }), p => ['users', p.userId, 'permissions', 'attach'])
      .input(z.object({ permission: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: z.object({ data: z.array(z.string()) }),
        }),
      ),

    // POST /users
    create: p
      .path('users')
      .input(
        userPhoneInputSchema
          .extend({
            company_id: z.number().nullish(),
            email: z.email(),
            last_name: z.string(),
            locale: z.enum(['en', 'pl']),
            name: z.string(),
            password: z.string().optional(),
            role: z.string(),
            source: z.string().optional(),
          })
          .superRefine((data, ctx) => {
            addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
          }),
      )
      .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

    // DELETE /users/:id
    delete: p
      .path(z.object({ id: z.number() }), p => ['users', p.id])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // POST /users/:id/permissions/detach
    detachPermission: p
      .path(z.object({ userId: z.number() }), p => ['users', p.userId, 'permissions', 'detach'])
      .input(z.object({ permission: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: z.object({ data: z.array(z.string()) }),
        }),
      ),

    get: p
      .path(
        z.object({
          user: z.object({
            id: z.number(),
          }),
        }),
        ({ user }) => ['users', user.id],
      )
      .query(({ path, upFetch }) =>
        upFetch(path, {
          schema: getDataWrapperSchema(GetUserSchema),
        }),
      ),

    // GET /users/:id/permissions
    getPermissions: p
      .path(z.object({ userId: z.number() }), p => ['users', p.userId, 'permissions'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          schema: z.object({
            data: z.object({
              direct: z.array(z.string()),
              via_roles: z.array(z.string()),
            }),
          }),
        }),
      ),

    // GET /users/:id/roles
    getRoles: p
      .path(z.object({ userId: z.number() }), p => ['users', p.userId, 'roles'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          schema: z.object({ data: z.array(z.object({ id: z.number(), name: z.string() })) }),
        }),
      ),

    getSimple: p
      .path(z.object({ id: z.number() }), p => ['users', p.id])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: {
            i: ['name', 'last_name'].join('|'),
          },
          schema: getDataWrapperSchema(GetUserSchema),
        }),
      ),

    // GET /users/:id
    getUser: p
      .path(z.object({ id: z.number() }), p => ['users', p.id])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: {
            i: ['last_auth'].join('|'),
          },
          schema: getDataWrapperSchema(
            GetUserSchema.extend({
              company: GetCompanySchema.nullish(),
              last_auth: GetLastAuthSchema.nullish(),
              role: GetRoleSchema,
              source: z.string(),
              status: GetUserStatusSchema,
            }),
          ),
        }),
      ),

    list: p
      .path('users')
      .input(
        z
          .object({
            'filter[statuses]': z.string().optional(),
            page: z.number().optional(),
            perPage: z.number().optional(),
            search: z.string().optional(),
          })
          .loose(),
      )
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: {
            i: ['last_auth', 'groups', 'updated_at', 'counters', 'company.type'].join('|'),
            ...input,
          },
          schema: GetPaginationSchema(
            GetUserSchema.extend({
              company: GetCompanySchema.extend({
                type: z.object({
                  alias: z.string(),
                  title: z.string(),
                }),
              }).nullish(),
              counters: z
                .object({
                  login_via_token: z.number(),
                })
                .or(z.array(z.unknown())),
              email: GetEmailSchema,
              last_auth: GetLastAuthSchema.nullish(),
              role: GetRoleSchema.nullish(),
              source: z.string().nullish(),
              status: GetStatusSchema,
            }),
          ),
        }),
      ),

    // GET /users/:role (e.g., /users/admin, /users/super-admin)
    listByRole: p
      .path(z.object({ role: z.string() }), p => ['users', p.role])
      .input(
        z
          .object({
            'filter[statuses]': z.string().optional(),
            page: z.number().optional(),
            perPage: z.number().optional(),
            search: z.string().optional(),
          })
          .passthrough(),
      )
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: {
            ...input,
            i: ['last_auth', 'groups', 'updated_at', 'counters', 'company.type'].join('|'),
          },
          schema: GetPaginationSchema(
            GetUserSchema.extend({
              company: GetCompanySchema.extend({
                type: z.object({
                  alias: z.string(),
                  title: z.string(),
                }),
              }).nullish(),
              counters: z
                .object({
                  login_via_token: z.number(),
                })
                .or(z.array(z.unknown())),
              email: GetEmailSchema,
              last_auth: GetLastAuthSchema.nullish(),
              role: GetRoleSchema,
              source: z.string().nullish(),
              status: GetStatusSchema,
            }),
          ),
        }),
      ),

    pageData: {
      // GET /users/:userId/pages/:page/data
      get: p
        .path(PageDataPathSchema, pageDataPath)
        .query(({ path, upFetch }) => upFetch(path, { schema: pageDataSchema.nullable() })),

      // POST /users/:userId/pages/:page/data: replaces the page's whole value. Loose, so a reply
      // that is the value itself (Saved Views accept it) keeps its keys.
      save: p
        .path(PageDataPathSchema, pageDataPath)
        .input(z.record(z.string(), z.unknown()))
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, { body: input, method: 'POST', schema: pageDataSchema.loose() }),
        ),
    },

    // PATCH /users/:id
    update: p
      .path(z.object({ id: z.number() }), p => ['users', p.id])
      .input(
        userPhoneInputSchema
          .extend({
            company_id: z.number().nullish(),
            email: z.email(),
            last_name: z.string(),
            locale: z.enum(['en', 'pl']),
            name: z.string(),
            role: z.string(),
            source: z.string(),
          })
          .partial()
          .superRefine((data, ctx) => {
            addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
          }),
      )
      .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

    // POST /users/:id/avatar (multipart form data)
    updateAvatar: p
      .path(z.object({ id: z.number() }), p => ['users', p.id, 'avatar'])
      .input(z.object({ avatar: z.instanceof(File) }))
      .mutation(({ input, path, upFetch }) => {
        const formData = new FormData()
        formData.append('avatar', input.avatar)

        return upFetch<{ data: { id: number; name: string; url: string } }>(path, {
          body: formData,
          method: 'POST',
        })
      }),

    // PATCH /users/:id/password
    updatePassword: p
      .path(z.object({ id: z.number() }), p => ['users', p.id, 'password'])
      .input(z.object({ password: z.string(), password_confirmation: z.string() }))
      .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

    // PATCH /users/:id/status/:statusId
    updateStatus: p
      .path(z.object({ id: z.number(), statusId: z.number() }), p => [
        'users',
        p.id,
        'status',
        p.statusId,
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'PATCH' })),
  }
}
